import React, { useCallback, useRef, useState, useMemo } from 'react';
import { Button, Form, Input, InputNumber, Select } from 'antd';
import {
  FilterOutlined,
  RedoOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import SelectPeopleGroup from '@aims/shared/people-groups/SelectPeopleGroup';
import SelectContact from '../contacts/SelectContact';
import SelectRegionForAdmin from '../people_groups/ListPeopleGroups/SelectRegionForAdmin';
import {
  HolisticProjectStages,
  HolisticProjectTypes,
} from '@aims/shared/holistic-projects/constants';
import ExportAllPaymentPlansBtn from './ExportAllPaymentPlansBtn';
import useProfile from '../../shared/use-profile';

function SelectHolisticProjectType({ disabled, onChange, value }) {
  return (
    <Select disabled={disabled} onChange={onChange} value={value} allowClear>
      {Object.entries(HolisticProjectTypes).map(([key, obj]) => (
        <Select.Option key={key} value={key}>
          {obj.label}
        </Select.Option>
      ))}
    </Select>
  );
}

const keyMap = {
  createdAt: 'createdAt',
  updatedAt: 'updatedAt',
  submittedAt: 'submittedAt',
  typeOfProject: 'typeOfProject.keyword',
  projectName: 'projectName.keyword',
  loanTotalUsd: 'loanTotalX4',
};

function FilterHolisticProjects({
  filters,
  refetch,
  reportSearch,
  loading,
  sortBy,
  type,
  parentFilters,
  style,
  projects,
}) {
  const [sortedBy, setSortedBy] = useState('updatedAt');
  const [sortOrder, setSortOrder] = useState('DESC');
  const [showFilters, setShowFilters] = useState(false);
  const filterChangeTimeout = useRef();


  const profile = useProfile();
  const permissions = profile?.permissions;
  const showProjects = useMemo(() => {
      return permissions?.find((p) => p.prefix === 'list-admin-h-project');
    }, [permissions]);

  const showExportBtn = showProjects;
  const handleSortByChange = useCallback(
    (value) => {
      sortBy.current = [
        { key: keyMap[value], order: sortOrder },
        { key: '_score', order: 'DESC' },
      ];
      setSortedBy(value);
      refetch();
    },
    [sortOrder, refetch, sortBy],
  );

  const handleSortOrderChange = useCallback(
    (value) => {
      sortBy.current = [
        { key: keyMap[sortedBy], order: value },
        { key: '_score', order: 'DESC' },
      ];
      setSortOrder(value);
      refetch();
    },
    [sortedBy, refetch, sortBy],
  );

  const onFiltersChanged = useCallback(
    (_changed, values) => {
      const { search, ...changed } = _changed;
      if (search != undefined) {
        sortBy.current = [
          { key: '_score', order: 'DESC' },
          {
            key: keyMap[sortedBy],
            order: sortOrder,
          },
        ];
        reportSearch(search.toLowerCase());
      } else {
        filters.current = {
          ...filters.current,
          ...Object.entries(changed).reduce((prev, [k, v]) => {
            prev[k] = v ? v : undefined;
            return prev;
          }, {}),
          humanReadableId: values.humanReadableId || -1,
          ...parentFilters,
        };
        sortBy.current = [
          {
            key: keyMap[sortedBy],
            order: sortOrder,
          },
          { key: '_score', order: 'DESC' },
        ];
        clearTimeout(filterChangeTimeout.current);
        filterChangeTimeout.current = setTimeout(() => {
          refetch();
        }, 500);
      }
    },
    [
      reportSearch,
      refetch,
      sortedBy,
      sortOrder,
      filters,
      sortBy,
      parentFilters,
    ],
  );

  return (
    <>
      <Form
        layout="vertical"
        style={{ marginBottom: 32, ...style }}
        onValuesChange={onFiltersChanged}
      >
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            marginBottom: 16,
          }}
        >
          <Form.Item
            name="search"
            style={{ maxWidth: 500, minWidth: 300, marginRight: 16 }}
          >
            <Input placeholder="Search" suffix={<SearchOutlined />} />
          </Form.Item>
          {showFilters && (
            <Button
              icon={<FilterOutlined />}
              onClick={() => {
                setShowFilters(false);
              }}
            >
              Hide Filters
            </Button>
          )}
          {!showFilters && (
            <Button
              icon={<FilterOutlined />}
              onClick={() => setShowFilters(true)}
            >
              Show Filters
            </Button>
          )}
          <div style={{ flex: 1, minWidth: 32 }} />
          {showExportBtn && <ExportAllPaymentPlansBtn projects={projects} />}
          <Button icon={<RedoOutlined />} onClick={refetch}>
            Refresh
          </Button>
        </div>
        {showFilters && (
          <>
            <div style={{ display: 'flex', flexWrap: 'wrap' }}>
              <Form.Item
                label="Project #"
                name="humanReadableId"
                style={{ flex: 1, marginRight: 16, minWidth: 200 }}
              >
                <InputNumber
                  min={0}
                  step={1}
                  disabled={loading}
                  style={{ width: '100%' }}
                />
              </Form.Item>
              <Form.Item
                label="Project Stage"
                name="stage"
                style={{ flex: 1, minWidth: 200, marginRight: 16 }}
              >
                <Select style={{ width: '100%' }} allowClear>
                  {Object.entries(HolisticProjectStages).map(([key, obj]) => (
                    <Select.Option key={obj.key} value={obj.key}>
                      {obj.label}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap' }}>
              <Form.Item
                label="Region"
                name="regionCode"
                style={{ flex: 1, minWidth: 200, marginRight: 16 }}
              >
                <SelectRegionForAdmin disabled={loading} />
              </Form.Item>
              <Form.Item
                label="People Group"
                name="peopleGroupId"
                style={{ flex: 1, minWidth: 200, marginRight: 16 }}
              >
                <SelectPeopleGroup disabled={loading} />
              </Form.Item>
              <Form.Item
                label="Sort By"
                style={{ flex: 2, marginRight: 16, minWidth: 200 }}
              >
                <Select
                  placeholder="Select ..."
                  value={sortedBy}
                  onChange={handleSortByChange}
                  disabled={loading}
                >
                  <Select.Option value="updatedAt">Last Updated</Select.Option>
                  <Select.Option value="createdAt">Created Date</Select.Option>
                  {type === 'holisticProposal' && (
                    <Select.Option value="submittedAt">
                      Submitted Date
                    </Select.Option>
                  )}
                  <Select.Option value="typeOfProject">
                    Type of Project
                  </Select.Option>
                  <Select.Option value="projectName">
                    Project Name
                  </Select.Option>
                  <Select.Option value="loanTotalUsd">Loan Total</Select.Option>
                </Select>
              </Form.Item>
              <Form.Item label="Sort Order" style={{ minWidth: 100 }}>
                <Select
                  placeholder="Select ..."
                  value={sortOrder}
                  onChange={handleSortOrderChange}
                  disabled={loading}
                >
                  <Select.Option value="ASC">Ascending</Select.Option>
                  <Select.Option value="DESC">Descending</Select.Option>
                </Select>
              </Form.Item>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap' }}>
              <Form.Item
                label="Regional Coordinator"
                name="regionalCoordId"
                style={{ flex: 1, minWidth: 200, marginRight: 16 }}
              >
                <SelectContact
                  disabled={loading}
                  filters={{
                    isRegionalCoord: true,
                    contactType: 'CONTACT',
                  }}
                />
              </Form.Item>
              <Form.Item
                label="Supported Field Worker"
                name="supportedFieldWorkerId"
                style={{ flex: 1, minWidth: 200, marginRight: 16 }}
              >
                <SelectContact
                  disabled={loading}
                  filters={{
                    isFieldWorker: true,
                    contactType: 'CONTACT',
                  }}
                />
              </Form.Item>
              <Form.Item
                label="Responsible Person"
                name="responsiblePersonId"
                style={{ flex: 1, minWidth: 200, marginRight: 16 }}
              >
                <SelectContact
                  disabled={loading}
                  filters={{
                    isFieldWorker: true,
                  }}
                />
              </Form.Item>
              <Form.Item
                label="Type of Project"
                name="typeOfProject"
                style={{ flex: 1, minWidth: 200 }}
              >
                <SelectHolisticProjectType disabled={loading} />
              </Form.Item>
            </div>
          </>
        )}
      </Form>
    </>
  );
}

export default FilterHolisticProjects;
